import { clientWithAuth } from 'dashboard409a/services/base';
import type { TypedDocumentString } from './generated/graphql';

// Django proxy that holds Alexandria's x-api-key server-side.
// Using clientWithAuth gives us the Bearer refresh and 429 backoff
// interceptors for free — a GraphQL call is just a POST.
const ALEXANDRIA_GRAPHQL_PATH = '/api/dashboard/market-data/graphql';
const ALEXANDRIA_REQUEST_TIMEOUT_MS = 35_000;

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

export class AlexandriaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AlexandriaError';
  }
}

interface AlexandriaRequestOptions {
  signal?: AbortSignal;
}

type AlexandriaQueryArgs<TVariables> =
  TVariables extends Record<string, never> ? [] : [variables: TVariables, options?: AlexandriaRequestOptions];

export const runAlexandriaQuery = async <TResult, TVariables>(
  query: TypedDocumentString<TResult, TVariables>,
  ...args: AlexandriaQueryArgs<TVariables>
): Promise<TResult> => {
  const [variables, options] = args as [TVariables | undefined, AlexandriaRequestOptions | undefined];
  const { data } = await clientWithAuth.post<GraphQLResponse<TResult>>(
    ALEXANDRIA_GRAPHQL_PATH,
    {
      query: query.toString(),
      variables,
    },
    {
      signal: options?.signal,
      timeout: ALEXANDRIA_REQUEST_TIMEOUT_MS,
    }
  );

  if (data.errors?.length) {
    throw new AlexandriaError(data.errors.map(e => e.message).join('; '));
  }
  if (!data.data) {
    throw new AlexandriaError('Empty response from Alexandria.');
  }
  return data.data;
};
